import React from 'react';
import DetectionGroupContext from '.';
import { useGetLiveDisplayQuery } from '../../../../../generated/core-api/graphql-react-query';
import useGlobalStoreContext from '../../../../Contexts/GlobalContext/Contexts/GlobalStoreContext/useGlobalStoreContext';
import useContentMaps from '../../../../LiveContext/Hooks/useSession/useContentMaps';
import { LatestDetection } from '../../../../LiveContext/NewSessionContext/Contexts/SessionDetectionContextProvider/useSessionDetectionContext';
import useSessionContext from '../../../../LiveContext/SessionContextProvider/useSessionContext';
import useDetectionVisibility from './Hooks/useDetectionVisibility';

interface DetectionBadgeContextProviderProps {
  children: React.ReactNode;
  tab: { index: number, updatedAt: number };
}

export default function DetectionGroupContextProvider(props: DetectionBadgeContextProviderProps) {
  const { children, tab } = props;
  const { latestDetection } = useSessionContext();
  const { projectId: [projectId], displayId: [displayId] } = useGlobalStoreContext();
  const display = useGetLiveDisplayQuery({ input: { id: displayId } }, { enabled: !!displayId });
  const shouldLoadContentMaps = !!projectId && !!(latestDetection || displayId);
  // The detection-group context only needs card/trigger labels once there is an
  // actual detection or displayed card to enrich. Loading those project maps on
  // first paint delays the entire live shell even though the session panel may
  // never be opened.
  const { cardTitleMap, commandTitleMap, isIntentionMap } = useContentMaps(shouldLoadContentMaps ? projectId : undefined);
  const [detections, setDetections] = React.useState<(LatestDetection & { cardTitle: string, commandTitle: string })[]>([]);
  React.useEffect(() => {
    if (latestDetection) {
      const cardTitle = cardTitleMap.get(latestDetection.cardId) ?? '';
      const commandTitle = commandTitleMap.get(latestDetection.commandId) ?? '';
      const isIntention = isIntentionMap.get(latestDetection.commandId) ?? undefined;
      const latestDetectionWithTitles = {
        ...latestDetection, cardTitle, commandTitle, isIntention,
      };
      setDetections((prev) => [...prev, { ...latestDetectionWithTitles }]);
    } else {
      setDetections([]);
    }
  }, [latestDetection]);

  const value = useDetectionVisibility(detections, tab, latestDetection, display.data?.getDisplay ?? null);

  return (
    <DetectionGroupContext.Provider value={value}>
      {children}
    </DetectionGroupContext.Provider>
  );
}
